import { Component } from '@theme/component';
import { cartQuantity, createLatestRead, createFrameTask } from '@theme/utilities';
import {
  canCommitCartWrite,
  nextCartCommitToken,
  operationToken,
  trackCartRender,
  runCartDomWrite,
  stampSurfaceRevision,
} from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  #historyRead = createLatestRead();
  #fallbackRead = createLatestRead();
  #connectionEpoch = 0;
  #bubbleRevision = 0;
  #count = null;
  #authoritative = false;
  #needsReconcile = false;
  #reconcileAfterToken = null;
  #fallbackTask = null;
  #pending = new Set();
  #animationTimer = null;
  #animationFrame = createFrameTask(() => {
    if (!this.isConnected) return;
    const revision = this.#bubbleRevision;
    const bubble = this.refs.cartBubble;
    bubble.classList.add('cart-bubble--animating');

    this.#animationTimer = setTimeout(() => {
      this.#animationTimer = null;
      if (this.#bubbleRevision === revision) bubble.classList.remove('cart-bubble--animating');
    }, 1000);
  });

  get currentCartCount() {
    return this.#count ?? Number(this.refs.cartBubbleCount.textContent || 0);
  }

  connectedCallback() {
    super.connectedCallback();
    ++this.#connectionEpoch;
    this.#authoritative = false;
    for (const type of this.#mutationEvents) document.addEventListener(type, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') this.#requestReconcile();
  }

  get #mutationEvents() {
    return [StandardEvents.cartLinesUpdate, StandardEvents.cartNoteUpdate, StandardEvents.cartDiscountUpdate];
  }

  disconnectedCallback() {
    ++this.#connectionEpoch;
    this.#historyRead.cancel();
    this.#fallbackRead.cancel();
    this.#fallbackTask = null;
    this.#cancelAnimation();
    this.#pending.clear();
    this.#needsReconcile = false;
    this.#reconcileAfterToken = null;
    for (const type of this.#mutationEvents) document.removeEventListener(type, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    super.disconnectedCallback();
  }

  #cancelAnimation() {
    ++this.#bubbleRevision;
    this.#animationFrame.cancel();
    clearTimeout(this.#animationTimer);
    this.#animationTimer = null;
    this.refs.cartBubble?.classList.remove('cart-bubble--animating');
  }

  onPageShow = (event) => {
    if (event.persisted) this.#requestReconcile();
  };

  onCartUpdate = (event) => {
    const promise = event.promise;
    if (!promise) return;
    this.#historyRead.cancel();
    const epoch = this.#connectionEpoch;
    const token = operationToken(promise);
    this.#pending.add(promise);
    const work = trackCartRender(promise, async (data) => {
      if (!this.isConnected || this.#connectionEpoch !== epoch) return;
      const count = data?.cart?.totalQuantity;
      if (Number.isSafeInteger(count) && count >= 0) {
        if (await this.renderCartBubble(count, true, token)) this.#clearReconcile(token);
        return;
      }

      this.#markReconcile(token);
      await this.#sharedFallback(epoch, token);
    });
    void work.catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-icon] Cart render failed:', error);
    }).finally(() => {
      if (this.#connectionEpoch !== epoch) return;
      this.#pending.delete(promise);
      if (this.#needsReconcile) this.#requestReconcile();
    });
  };

  renderCartBubble = async (itemCount, animate = true, token = null) => {
    cartQuantity(itemCount);
    if (!this.isConnected || !canCommitCartWrite(this, token)) return false;
    this.#cancelAnimation();
    const { cartBubble, cartBubbleCount } = this.refs;
    runCartDomWrite({ currentElement: this, incomingContent: null }, () => {
      cartBubbleCount.classList.toggle('hidden', itemCount === 0);
      cartBubble.classList.toggle('visually-hidden', itemCount === 0);
      cartBubbleCount.textContent = itemCount < 100 ? String(itemCount) : '';
      this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
      if (token !== null) stampSurfaceRevision(this, token);
      this.#count = itemCount;
      if (token !== null) this.#authoritative = true;
    });
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(itemCount), timestamp: Date.now() }));
    } catch {

    }
    if (animate && itemCount > 0) this.#animationFrame.schedule();
    return true;
  };

  ensureCartBubbleIsCorrect = () => {
    let count;
    try {
      const stored = JSON.parse(sessionStorage.getItem('cart-count'));
      if (!stored || !/^\d+$/.test(stored.value)) return;
      count = Number(stored.value);
      const age = Date.now() - stored.timestamp;
      if (!Number.isSafeInteger(count) || count < 0 || !Number.isFinite(stored.timestamp) || age < 0 || age >= 10000) return;
    } catch {
      return;
    }
    if (!this.#authoritative) {
      void this.renderCartBubble(count, false).catch((error) => console.warn('[cart-icon] Hint render failed:', error));
    }
  };

  #markReconcile(token) {
    this.#needsReconcile = true;
    if (this.#reconcileAfterToken === null || token > this.#reconcileAfterToken) {
      this.#reconcileAfterToken = token;
    }
  }

  #clearReconcile(token) {
    if (this.#reconcileAfterToken === null || token >= this.#reconcileAfterToken) {
      this.#reconcileAfterToken = null;
      this.#needsReconcile = false;
    }
  }

  #sharedFallback(epoch, token) {
    if (this.#fallbackTask) return this.#fallbackTask;
    const read = this.#fallbackRead.begin();
    const task = this.#runFallback(epoch, token, read).finally(() => {
      read.finish();
      if (this.#fallbackTask === task) this.#fallbackTask = null;
    });
    this.#fallbackTask = task;
    return task;
  }

  async #runFallback(epoch, token, read) {
    let count;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: read.signal,
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      count = cartQuantity((await response.json()).item_count);
    } catch {

      return;
    }
    if (!read.isCurrent() || !this.isConnected || this.#connectionEpoch !== epoch) return;
    if (await this.renderCartBubble(count, false, token)) this.#clearReconcile(token);
  }

  #requestReconcile = () => {
    this.#needsReconcile = true;
    if (!this.isConnected || this.#pending.size) return;
    const pendingQuantities = customElements.get('cart-items-component')?.pendingQuantityUpdates ?? [];
    if (pendingQuantities.length) {
      const epoch = this.#connectionEpoch;
      void Promise.allSettled(pendingQuantities).then(() => {
        if (this.isConnected && this.#connectionEpoch === epoch && this.#needsReconcile) this.#requestReconcile();
      });
      return;
    }
    void this.#reconcileHistoryCart();
  };

  #reconcileHistoryCart = async () => {
    if (!this.isConnected) return;
    const read = this.#historyRead.begin();
    const epoch = this.#connectionEpoch;
    const token = nextCartCommitToken();
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: read.signal,
      });
      if (!read.isCurrent() || this.#connectionEpoch !== epoch) return;
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await response.json();
      if (!read.isCurrent() || !this.isConnected || this.#connectionEpoch !== epoch) return;
      const count = cartQuantity(cart.item_count);
      if (await this.renderCartBubble(count, false, token)) this.#clearReconcile(token);
    } catch (error) {
      if (read.isCurrent() && error?.name !== 'AbortError') console.warn('[cart-icon] History reconciliation failed:', error);
    } finally {
      read.finish();
    }
  };
}

if (!customElements.get('cart-icon')) customElements.define('cart-icon', CartIcon);
